import React, { useEffect, useState } from "react";
import { Col, Row, Container, Card } from "react-bootstrap";
import './Analytics.css'; // Import custom CSS for additional styling
import { API_URL } from "./config";
import DashboardLayout from "./DashboardLayout/DasboardLayout";

// Rebuilt 2026-09-14 — every figure on this screen used to be hardcoded JSX text (no fetch at all).
// Now sourced from `/application_statistics/get_last_statistic`, the same combined
// private+rideshare snapshot AppStatistics.js/cards.js already use correctly, plus a real average
// driver rating computed from `/driver`.
const Analytics = () => {
  const [stats, setStats] = useState(null);
  const [avgDriverRating, setAvgDriverRating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, driversRes] = await Promise.all([
          fetch(`${API_URL}/application_statistics/get_last_statistic`),
          fetch(`${API_URL}/driver/`),
        ]);

        if (statsRes.ok) {
          setStats(await statsRes.json());
        } else if (statsRes.status !== 404) {
          throw new Error(`Statistics request failed: ${statsRes.status}`);
        }

        if (driversRes.ok) {
          const drivers = await driversRes.json();
          const ratings = drivers
            .map((d) => parseFloat(d.rating))
            .filter((r) => Number.isFinite(r) && r > 0);
          setAvgDriverRating(
            ratings.length > 0 ? ratings.reduce((sum, r) => sum + r, 0) / ratings.length : null
          );
        }
      } catch (err) {
        console.error("Error fetching analytics:", err);
        setError("Failed to load analytics data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const totalTrips = stats?.total_trips ?? 0;
  const privateTrips = stats?.trips?.private?.count ?? 0;
  const rideshareTrips = stats?.trips?.rideshare?.count ?? 0;
  const avgTripValue = stats?.average_trip_rate_usd != null ? Number(stats.average_trip_rate_usd) : null;
  const tripsPerCustomer = stats?.trips_per_customer != null ? Number(stats.trips_per_customer) : null;
  const newDriversToday = stats?.new_drivers?.total ?? 0;

  return (
    <DashboardLayout>
      <Container fluid>
        <Row>
          <Col md={12}>
            <Card className="analytics-card">
              <Card.Body>
                <Card.Title className="text-center">
                  <h1 className="my-4">Analytics Overview</h1>
                  {stats?.date && (
                    <p className="text-muted" style={{ fontSize: "0.9rem" }}>
                      Latest snapshot: {stats.date}
                    </p>
                  )}
                </Card.Title>

                {loading ? (
                  <p className="text-center">Loading…</p>
                ) : error ? (
                  <p className="text-center text-danger">{error}</p>
                ) : !stats ? (
                  <p className="text-center">No statistics recorded yet.</p>
                ) : (
                  <Card.Text>
                    <div className="analytics-item">
                      <strong>Total Trips (Private + Rideshare):</strong>
                      <span className="value">{totalTrips}</span>
                    </div>
                    <div className="analytics-item">
                      <strong>&nbsp;&nbsp;— Private:</strong>
                      <span className="value">{privateTrips}</span>
                    </div>
                    <div className="analytics-item">
                      <strong>&nbsp;&nbsp;— Rideshare:</strong>
                      <span className="value">{rideshareTrips}</span>
                    </div>
                    <div className="analytics-item">
                      <strong>Average Trip Value:</strong>
                      <span className="value">
                        {avgTripValue != null ? `$${avgTripValue.toFixed(2)}` : "N/A"}
                      </span>
                    </div>
                    <div className="analytics-item">
                      <strong>Trips Per Customer:</strong>
                      <span className="value">
                        {tripsPerCustomer != null ? tripsPerCustomer.toFixed(2) : "N/A"}
                      </span>
                    </div>
                    <div className="analytics-item">
                      <strong>New Drivers Today:</strong>
                      <span className="value">{newDriversToday}</span>
                    </div>
                    <div className="analytics-item">
                      <strong>Average Driver Rating:</strong>
                      <span className="value">
                        {avgDriverRating != null ? `${avgDriverRating.toFixed(1)} ⭐` : "N/A"}
                      </span>
                    </div>
                  </Card.Text>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </DashboardLayout>
  );
};

export default Analytics;
